import { useMemo, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton, XStack, YStack } from '@/components/ui';
import { InitialsAvatar } from '@/components/ui/Avatar';
import { Command } from '@/components/ui/cmdk/cmdk';
import { Popover } from '@/components/ui/popover';
import { classList } from '@/utils/cn';

import {
  isAssignableSpeakerLabel,
  normalizeSpeakerLabel,
  renameTranscriptSpeaker,
} from './meetings-speaker-rename';
import { getAttendeeDisplayName } from './meetings-transcript-display.hooks';

import type { CalendarEventAttendee, MeetingId } from './meetings.types';

const LABEL_CLASS_NAME =
  'px-2 text-[11px] leading-none text-foreground-muted no-record';

interface TranscriptSpeakerLabelProps {
  meetingId: MeetingId;
  displayLabel: string;
  transcriptLabel: string | null;
  attendees?: CalendarEventAttendee[] | null;
  speakerLabels: string[];
  isOthers: boolean;
  editable: boolean;
}

export const TranscriptSpeakerLabel = ({
  meetingId,
  displayLabel,
  transcriptLabel,
  attendees,
  speakerLabels,
  isOthers,
  editable,
}: TranscriptSpeakerLabelProps) => {
  const canEdit = editable && !!transcriptLabel && transcriptLabel !== 'You';

  if (!canEdit) {
    return (
      <div
        className={classList(
          'mb-2',
          LABEL_CLASS_NAME,
          isOthers ? 'text-left' : 'text-right'
        )}
      >
        {displayLabel}
      </div>
    );
  }

  return (
    <SpeakerRenamePopover
      meetingId={meetingId}
      displayLabel={displayLabel}
      transcriptLabel={transcriptLabel}
      attendees={attendees}
      speakerLabels={speakerLabels}
      isOthers={isOthers}
    />
  );
};

interface SpeakerRenamePopoverProps {
  meetingId: MeetingId;
  displayLabel: string;
  transcriptLabel: string;
  attendees?: CalendarEventAttendee[] | null;
  speakerLabels: string[];
  isOthers: boolean;
}

const SpeakerRenamePopover = ({
  meetingId,
  displayLabel,
  transcriptLabel,
  attendees,
  speakerLabels,
  isOthers,
}: SpeakerRenamePopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { attendeeOptions, speakerOptions } = useMemo(
    () => buildSpeakerOptions(attendees, speakerLabels),
    [attendees, speakerLabels]
  );

  const typedName = normalizeSpeakerLabel(query);
  const normalizedQuery = typedName.toLowerCase();
  const matchesQuery = (option: SpeakerOption) =>
    !normalizedQuery ||
    option.name.toLowerCase().includes(normalizedQuery) ||
    !!option.email?.toLowerCase().includes(normalizedQuery);
  const visibleAttendees = attendeeOptions.filter(matchesQuery);
  const visibleSpeakers = speakerOptions.filter(matchesQuery);
  const hasExactMatch = [...attendeeOptions, ...speakerOptions].some(
    (option) => option.name.toLowerCase() === normalizedQuery
  );
  const showTypedOption =
    !!typedName && isAssignableSpeakerLabel(typedName) && !hasExactMatch;
  const isEmpty =
    !visibleAttendees.length && !visibleSpeakers.length && !showTypedOption;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) setQuery('');
  };

  const assign = async (name: string) => {
    if (isSaving) return;
    if (name === transcriptLabel) {
      handleOpenChange(false);
      return;
    }
    setIsSaving(true);
    const renamed = await renameTranscriptSpeaker(
      meetingId,
      transcriptLabel,
      name
    );
    setIsSaving(false);
    if (renamed) handleOpenChange(false);
  };

  const assignedNames = new Set(speakerLabels);
  const renderOption = (option: SpeakerOption, showAssignedHint: boolean) => (
    <Command.Item
      key={option.name}
      value={option.name}
      disabled={isSaving}
      className="gap-2"
      onSelect={() => void assign(option.name)}
    >
      <InitialsAvatar
        name={option.name}
        seed={option.email ?? option.name}
        className="size-5 text-[0.5rem]"
      />
      <YStack className="min-w-0 flex-1">
        <span className="truncate">{option.name}</span>
        {option.email && (
          <span className="truncate text-[10px] text-foreground-muted">
            {option.email}
          </span>
        )}
      </YStack>
      {option.name === displayLabel ? (
        <Icons.check className="size-3.5 shrink-0 opacity-75" />
      ) : (
        showAssignedHint &&
        assignedNames.has(option.name) && (
          <span className="shrink-0 text-[10px] text-foreground-muted">
            assigned
          </span>
        )
      )}
    </Command.Item>
  );

  return (
    <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Popover.Anchor asChild>
        <XStack center className="mb-2 h-[11px]">
          <button
            type="button"
            aria-label={`Rename speaker ${displayLabel}`}
            className={classList(
              LABEL_CLASS_NAME,
              'rounded-sm transition-colors hover:text-foreground',
              isOpen && 'text-foreground'
            )}
            onClick={() => handleOpenChange(true)}
          >
            {displayLabel}
          </button>
          <Popover.Trigger asChild>
            <IconButton
              icon={Icons.edit}
              size="xs"
              scale={0.85}
              type="button"
              aria-label="Rename speaker"
              className={classList(
                '-ml-1.5',
                'opacity-0 touch:opacity-100 group-hover/transcript-line:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]/transcript-line:opacity-100',
                'data-[state=open]:opacity-100',
                'transition-opacity duration-150'
              )}
            />
          </Popover.Trigger>
        </XStack>
      </Popover.Anchor>
      <Popover.Content
        align={isOthers ? 'start' : 'end'}
        className="w-64 p-0 no-record"
      >
        <Command.Root shouldFilter={false}>
          <Command.Input
            autoFocus
            placeholder="Search or type a name"
            maxLength={100}
            value={query}
            onValueChange={setQuery}
          />
          <Command.List>
            {isEmpty && (
              <div className="px-3 py-4 text-center text-xs text-foreground-subtle">
                Type a name to assign this speaker
              </div>
            )}
            {visibleAttendees.length > 0 && (
              <Command.Group heading="Attendees">
                {visibleAttendees.map((option) => renderOption(option, true))}
              </Command.Group>
            )}
            {visibleSpeakers.length > 0 && (
              <Command.Group heading="Other speakers">
                {visibleSpeakers.map((option) => renderOption(option, false))}
              </Command.Group>
            )}
            {showTypedOption && (
              <Command.Group>
                <Command.Item
                  value={`use:${typedName}`}
                  disabled={isSaving}
                  className="gap-2"
                  onSelect={() => void assign(typedName)}
                >
                  <Icons.plus className="size-3.5 shrink-0 opacity-75" />
                  <span className="truncate">
                    Use &ldquo;{typedName}&rdquo;
                  </span>
                </Command.Item>
              </Command.Group>
            )}
          </Command.List>
        </Command.Root>
      </Popover.Content>
    </Popover.Root>
  );
};

interface SpeakerOption {
  name: string;
  email?: string | null;
}

const GENERIC_SPEAKER_LABEL = /^Speaker \d+$/;

function buildSpeakerOptions(
  attendees: CalendarEventAttendee[] | null | undefined,
  speakerLabels: string[]
): { attendeeOptions: SpeakerOption[]; speakerOptions: SpeakerOption[] } {
  const attendeeOptions: SpeakerOption[] = [];
  const attendeeNames = new Set<string>();
  for (const attendee of attendees ?? []) {
    if (attendee.self || attendee.resource) continue;
    const name = getAttendeeDisplayName(attendee);
    if (!name || !isAssignableSpeakerLabel(name) || attendeeNames.has(name)) {
      continue;
    }
    attendeeNames.add(name);
    attendeeOptions.push({ name, email: attendee.email });
  }

  const speakerOptions = speakerLabels
    .filter(
      (label) =>
        !attendeeNames.has(label) &&
        isAssignableSpeakerLabel(label) &&
        !GENERIC_SPEAKER_LABEL.test(label)
    )
    .map((name) => ({ name }));

  return { attendeeOptions, speakerOptions };
}
